import { useMemo, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/base/PageHeader";
import SectionHeading from "@/components/base/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { chapters, arcProgress, chapterStats } from "@/mocks/chapters";
import { Link } from "react-router-dom";

const statusStyle: Record<string, string> = {
  已收录: "bg-secondary-100 text-secondary-800",
  更新中: "bg-accent-100 text-accent-800",
  完结: "bg-primary-100 text-primary-700",
};

const tips = [
  {
    icon: "ri-calendar-check-line",
    title: "追更节奏",
    text: "主篇剧情已整理至完结篇，后续若有续作或番外更新，本站会持续跟进并更新进度。",
  },
  {
    icon: "ri-search-line",
    title: "如何定位",
    text: "按剧情阶段筛选话数，即可快速定位当前追到哪、后面还有哪些阶段要看。",
  },
  {
    icon: "ri-mail-send-line",
    title: "不错过更新",
    text: "订阅追更提醒，当进度更新时会第一时间通过邮件通知你，无需每天手动检查。",
  },
];

export default function Chapters() {
  const [active, setActive] = useState("全部");
  const arcs = useMemo(
    () => ["全部", ...Array.from(new Set(chapters.map((c) => c.arc)))],
    [],
  );
  const list = useMemo(
    () => (active === "全部" ? chapters : chapters.filter((c) => c.arc === active)),
    [active],
  );

  useSeo({
    title: "斗罗大陆漫画追更话数进度 · 第几话速查",
    description:
      "斗罗大陆漫画更新到哪了、第几话？这里按剧情阶段整理主篇话数与追更进度，已收录至完结篇，快速定位当前追更进度不迷路。",
    keywords: "斗罗大陆漫画第几话,斗罗大陆漫画更新到哪了,斗罗大陆漫画话数,斗罗大陆漫画追更,斗罗大陆漫画进度",
    canonicalPath: "/chapters",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://dcomx.github.io/" },
          { "@type": "ListItem", position: 2, name: "追更话数", item: "https://dcomx.github.io/chapters" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "斗罗大陆漫画追更话数进度",
        url: "https://dcomx.github.io/chapters",
        inLanguage: "zh-CN",
        mainEntity: {
          "@type": "ItemList",
          itemListElement: chapters.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: `${c.no} ${c.title}`,
          })),
        },
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <PageHeader
          breadcrumbs={[{ label: "首页", to: "/" }, { label: "追更话数" }]}
          title="追更话数 · 第几话速查"
          subtitle="主篇《斗罗大陆》漫画已整理收录至完结篇，按剧情阶段速查关键话数与追更进度，快速定位当前进度。"
          image="https://readdy.ai/api/search-image?query=Ancient%20scroll%20with%20rows%20of%20glowing%20calligraphy%20chapter%20marks%20floating%20above%20misty%20mountains%2C%20chinese%20fantasy%20catalog%20aesthetic%2C%20warm%20amber%20and%20crimson%20tone%2C%20ink%20wash%20mixed%20with%20vibrant%20color%2C%20high%20detail&width=1600&height=640&seq=chapters-header&orientation=landscape"
          alt="斗罗大陆漫画话数追踪背景"
        />

        <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            kicker="阶段进度"
            title="主篇六大阶段进度一览"
            desc="每个剧情阶段的话数整理进度与完成情况，一眼看清整个主篇的推进全貌。"
          />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {arcProgress.map((p) => (
              <div key={p.name} className="rounded-lg border border-background-200 bg-background-100 p-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground-950">{p.name}</span>
                  <span className="text-xs font-bold text-primary-600">{p.percent}%</span>
                </div>
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-background-200">
                  <div className="h-full rounded-full bg-primary-500" style={{ width: `${p.percent}%` }} />
                </div>
              </div>
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-2xl text-center text-xs leading-relaxed text-foreground-500">
            主篇规模 {chapterStats.total}，本站已整理收录关键话数 {chapterStats.covered} 话。
          </p>
        </section>

        <section className="bg-background-100 py-14 md:py-20">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  话数速查列表
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  点击下方阶段标签可筛选，快速定位当前追到哪一话。
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {arcs.map((arc) => (
                  <button
                    key={arc}
                    type="button"
                    onClick={() => setActive(arc)}
                    className={`rounded-full px-1 py-1 transition-colors ${
                      active === arc ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                    }`}
                  >
                    <span className="px-3 py-1 text-xs font-medium whitespace-nowrap">{arc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="hidden grid-cols-12 gap-4 border-b border-background-200 bg-background-100 px-5 py-3 text-xs font-semibold text-foreground-500 sm:grid">
                <span className="col-span-2">话数</span>
                <span className="col-span-3">剧情阶段</span>
                <span className="col-span-5">话目标题</span>
                <span className="col-span-2 text-right">状态</span>
              </div>
              <div className="divide-y divide-background-100">
                {list.map((c) => (
                  <div
                    key={c.no}
                    className="grid grid-cols-12 items-center gap-4 px-5 py-4 transition-colors hover:bg-background-100/60"
                  >
                    <span className="col-span-3 font-heading text-sm font-bold text-primary-600 sm:col-span-2">
                      {c.no}
                    </span>
                    <span className="col-span-9 text-xs text-foreground-500 sm:col-span-3">{c.arc}</span>
                    <span className="hidden text-sm font-medium text-foreground-900 sm:col-span-5 sm:block">
                      {c.title}
                    </span>
                    <span className="col-span-12 text-left text-sm text-foreground-600 sm:hidden">
                      {c.title}
                    </span>
                    <span className="col-span-12 text-left sm:col-span-2 sm:text-right">
                      <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ${statusStyle[c.status]}`}>
                        {c.status}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            kicker="追更小贴士"
            title="追更不迷路的三个要点"
            desc="掌握节奏、快速定位、及时提醒，让追更更省心。"
          />
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {tips.map((t) => (
              <div key={t.title} className="rounded-lg border border-background-200 bg-background-100 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-500/15 text-accent-600">
                  <i className={`${t.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-lg bg-primary-500 p-6 text-center md:p-8">
            <h3 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
              想第一时间知道剧情更新？
            </h3>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-background-100/85">
              订阅追更提醒，进度更新时我们会通过邮件第一时间通知你。
            </p>
            <Link
              to="/#subscribe"
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
            >
              去订阅追更提醒
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}